import { createTheme, adaptV4Theme } from '@mui/material/styles';

const defaultTheme = createTheme();

let theme = createTheme(
  adaptV4Theme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 320,
        md: 600,
        lg: 1024,
        xl: 1330,
        xxl: 1920,
      },
    },
    mixins: {
      toolbar: {
        '@media (min-width:0)': {
          minHeight: defaultTheme.typography.pxToRem(56),
        },
      },
    },
    palette: {
      primary: {
        light: '#F4DA95',
        main: '#EDC14F',
        dark: '#E5AC17',
        A000: '#FFFCF3',
        contrastText: '#000',
      },
      secondary: {
        light: '#777777',
        main: '#000000',
        dark: '#000000',
      },
      error: {
        light: '#F6685E',
        main: '#F44336',
        dark: '#C3352B',
        A000: '#FFF5F5',
      },
      notice: {
        background: '#572627',
        backgroundDark: '#141312',
      },
      warning: {
        light: '#F5E67E',
        main: '#F3E05E',
        dark: '#C2B34B',
      },
      success: {
        light: '#81C784',
        main: '#4caf50',
        dark: '#1B9657',
      },
      info: {
        light: '#8BD0CB',
        main: '#00B0AA',
        dark: '#019D9D',
        800: '#017A83',
        100: '#EDF9F9',
        200: '#B2E0DC',
      },
      facebook: {
        main: '#4162a7',
      },
      grey: {
        A000: '#F9F9F9',
        50: '#EFEFEF',
        100: '#DADADA',
        200: '#AEAEAE',
        300: '#797979',
        400: '#5D5D5D',
        500: '#3A3A3A',
        600: '#303030',
        700: '#242424',
        800: '#1D1D1D',
        900: '#111111',
        pavement: '#2F2F2F',
        concrete: '#BBBCBC',
        3: '#828282',
      },
      orange: {
        100: '#FBF3DC',
        200: '#F8E6B9',
        300: '#FFAC71',
        400: '#FF9547',
        900: '#C74900',
      },
      common: {
        fogWhite: '#EBF2ED',
      },
      text: {
        primary: '#000000',
      },
      background: {
        primary: '#FFFCF3',
      },
    },
    typography: {
      fontFamily: "'Acumin Pro', sans-serif",
      fontFamilySecondary: "'DrukTextWide', serif",
      body1: {
        fontFamily: "'Acumin Pro', sans-serif",
        fontWeight: 400,
        fontSize: '1.125rem',
      },
      body2: {
        fontFamily: "'Acumin Pro', sans-serif",
        fontWeight: 400,
        fontSize: '0.875rem',
        lineHeight: '1.4375rem',
      },
    },
    overrides: {
      MuiCssBaseline: {
        '@global': {
          body: {
            fontFamily: "'Acumin Pro', sans-serif",
          },
          '.MuiOutlinedInput-input.Mui-disabled': {
            background: 'rgba(216, 216, 216, 0.15)',
          },
        },
      },
      MuiOutlinedInput: {
        root: {
          // borderRadius: 0,
        },
      },
      MuiDialogTitle: {
        root: {
          backgroundColor: '#efefef',
          alignItems: 'center',
          display: 'flex',
          padding: defaultTheme.typography.pxToRem(12),
          "& [class*='MuiTypography-root']": {
            margin: defaultTheme.spacing(0, 1),
            fontWeight: 500,
            flexGrow: 1,
            fontSize: defaultTheme.typography.pxToRem(20),
          },
        },
      },

      MuiPickersDay: {
        dayDisabled: {
          textDecoration: 'line-through',
        },
      },
    },
    props: {
      MuiLink: {
        underline: 'none',
        color: 'inherit',
      },
      MuiContainer: {
        maxWidth: 'xl',
      },
      MuiDialogTitle: {
        disableTypography: true,
      },
      MuiButton: {
        disableElevation: true,
        color: 'black',
      },
      MuiTextField: {
        color: 'secondary',
      },
    },
    spacing: (factor) => `${0.5 * factor}rem`,
  }),
);

theme.overrides = {
  MuiAlert: {
    root: {
      color: theme.palette.secondary.main,
      padding: theme.spacing(0.5, 1),
      '& $icon': {
        marginRight: theme.spacing(1),
        '& svg': {
          fontSize: '22px',
          [theme.breakpoints.down('lg')]: {
            fontSize: '20px',
          },
        },
      },
      '& $message': {
        paddingBottom: theme.spacing(0.75),
        [theme.breakpoints.down('lg')]: {
          fontSize: defaultTheme.typography.pxToRem(12),
          padding: theme.spacing(0.75, 0),
        },
      },
    },
    standardError: {
      color: theme.palette.secondary.main,
      backgroundColor: 'rgba(244, 67, 54, 0.1)',
      '& $icon': {
        color: theme.palette.error.main,
      },
    },
    standardInfo: {
      color: theme.palette.secondary.main,
      backgroundColor: 'rgba(139, 208, 203, 0.1)',
      '& $icon': {
        color: theme.palette.info.main,
      },
    },
    standardWarning: {
      color: theme.palette.secondary.main,
      backgroundColor: 'rgba(243, 224, 94, 0.1)',
      '& $icon': {
        color: theme.palette.warning.dark,
      },
    },
    standardSuccess: {
      color: theme.palette.secondary.main,
      backgroundColor: 'rgba(76, 175, 80, 0.1)',
      '& $icon': {
        color: theme.palette.success.main,
      },
    },
  },
  MuiDivider: {
    root: {
      backgroundColor: theme.palette.grey['100'],
    },
    light: {
      backgroundColor: theme.palette.grey['50'],
    },
  },
  MuiContainer: {
    root: {
      [theme.breakpoints.up('xl')]: {
        maxWidth: theme.spacing(220),
      },
      [theme.breakpoints.down('xl')]: {
        paddingLeft: theme.typography.pxToRem(52),
        paddingRight: theme.typography.pxToRem(52),
      },
      [theme.breakpoints.down('lg')]: {
        paddingLeft: theme.typography.pxToRem(38),
        paddingRight: theme.typography.pxToRem(38),
      },
      [theme.breakpoints.down('md')]: {
        paddingLeft: theme.typography.pxToRem(23),
        paddingRight: theme.typography.pxToRem(23),
      },
    },
  },
};

theme.typography.h1 = {
  fontFamily: "'DrukTextWide', sans-serif",
  fontWeight: 500,
  fontSize: '3rem',
  lineHeight: '3.75rem',
  letterSpacing: 0,
  [theme.breakpoints.down('lg')]: {
    // mobile
    fontSize: '1.75rem',
    lineHeight: '2.5rem',
  },
};

theme.typography.h2 = {
  fontFamily: "'DrukTextWide', sans-serif",
  fontWeight: 400,
  fontSize: '2.375rem',
  lineHeight: '2.875rem',
  [theme.breakpoints.down('lg')]: {
    // mobile
    fontSize: '1.5rem',
    lineHeight: '2.25rem',
  },
};

theme.typography.h3 = {
  fontFamily: "'DrukTextWide', sans-serif",
  fontWeight: 400,
  fontSize: '2.25rem',
  lineHeight: '2.475rem',
  [theme.breakpoints.down('xl')]: {
    fontSize: '2rem',
    lineHeight: '2.2rem',
  },
  [theme.breakpoints.down('lg')]: {
    fontSize: '1.75rem',
    lineHeight: '1.925rem',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '1.5rem',
    lineHeight: '1.65rem',
  },
};

theme.typography.h4 = {
  fontFamily: "'DrukTextWide', sans-serif",
  fontWeight: 400,
  fontSize: '1.3125rem',
  lineHeight: '2rem',
  [theme.breakpoints.down('lg')]: {
    // mobile
    fontSize: '1.125rem',
    lineHeight: '2rem',
  },
};

theme.typography.h5 = {
  fontFamily: "'DrukTextWide', sans-serif",
  fontWeight: 700,
  fontSize: theme.typography.pxToRem(27),
  lineHeight: '110%',
  textTransform: 'uppercase',
  [theme.breakpoints.down('lg')]: {
    // mobile
    fontSize: theme.typography.pxToRem(21),
    lineHeight: '110%',
  },
};

theme.typography.h6 = {
  fontFamily: "'DrukTextWide', sans-serif",
  fontWeight: 700,
  fontSize: theme.typography.pxToRem(20),
  lineHeight: '110%',
  textTransform: 'uppercase',
  [theme.breakpoints.down('xl')]: {
    fontSize: theme.typography.pxToRem(19),
  },
  [theme.breakpoints.down('lg')]: {
    fontSize: theme.typography.pxToRem(18),
  },
  [theme.breakpoints.down('md')]: {
    fontSize: theme.typography.pxToRem(17),
  },
};

theme.typography.subtitle1 = {
  fontFamily: "'Acumin Pro', sans-serif",
  fontWeight: 300,
  fontSize: '1.125rem',
  lineHeight: '1.5rem',
  [theme.breakpoints.down('lg')]: {
    // mobile
    fontSize: '1rem',
    lineHeight: '1.5rem',
  },
};

theme.typography.subtitle2 = {
  fontFamily: "'Acumin Pro', sans-serif",
  fontWeight: 300,
  fontSize: '1rem',
  lineHeight: '1.5rem',
  [theme.breakpoints.down('lg')]: {
    // mobile
    fontSize: '0.875rem',
    lineHeight: '1.5rem',
  },
};
theme.typography.body1 = {
  fontFamily: "'Acumin Pro', sans-serif",
  fontWeight: 400,
  fontSize: '1.125rem',
  lineHeight: '150%',
  [theme.breakpoints.down('xl')]: {
    fontSize: '1.25rem',
    lineHeight: '2.25rem',
  },
  [theme.breakpoints.down('lg')]: {
    fontSize: '1.1875rem',
    lineHeight: '2.1375rem',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '1rem',
    lineHeight: '150%',
  },
};
theme.typography.body2 = {
  fontFamily: "'Acumin Pro', sans-serif",
  fontWeight: 400,

  fontSize: theme.typography.pxToRem(18),
  lineHeight: theme.typography.pxToRem(27),
  [theme.breakpoints.down('xl')]: {
    fontSize: '1.25rem',
    lineHeight: '2.25rem',
  },

  [theme.breakpoints.down('md')]: {
    fontSize: theme.typography.pxToRem(16),
    lineHeight: theme.typography.pxToRem(24),
  },
};
theme.typography.bodyEyebrow = {
  fontFamily: "'Acumin Pro', sans-serif",
  fontWeight: 400,
  fontSize: theme.typography.pxToRem(14),
  lineHeight: '120%',

  [theme.breakpoints.down('lg')]: {
    fontSize: theme.typography.pxToRem(13),
  },
};
theme.typography.bodyXL = {
  fontFamily: "'Acumin Pro', sans-serif",
  fontWeight: 400,
  fontSize: theme.typography.pxToRem(21),
  lineHeight: '180%',
  display: 'block',
  [theme.breakpoints.down('xl')]: {
    fontSize: theme.typography.pxToRem(20),
  },
  [theme.breakpoints.down('lg')]: {
    fontSize: theme.typography.pxToRem(19),
  },
  [theme.breakpoints.down('md')]: {
    fontSize: theme.typography.pxToRem(18),
  },
};
// theme = responsiveFontSizes(theme);
theme = adaptV4Theme(theme);
theme.components.MuiTextField = {
  defaultProps: {
    color: 'secondary',
  },
};
theme.components.MuiButton = {
  styleOverrides: {
    root: {
      padding: theme.spacing(2, 5),
      fontWeight: 700,
      fontSize: theme.typography.pxToRem(15),
      lineHeight: theme.typography.pxToRem(15),
      letterSpacing: theme.typography.pxToRem(0.75),
      [theme.breakpoints.down('xl')]: {
        padding: theme.typography.pxToRem(15, 36),
      },
      [theme.breakpoints.down('md')]: {
        padding: theme.typography.pxToRem(13, 28),
        fontSize: theme.typography.pxToRem(14),
        lineHeight: theme.typography.pxToRem(14),
        letterSpacing: theme.typography.pxToRem(0.7),
      },
    },
  },

  variants: [
    {
      props: { color: 'primary' },
      style: {
        color: theme.palette.secondary.main,
      },
    },
    {
      props: { color: 'primary', variant: 'contained' },
      style: {
        color: theme.palette.secondary.main,
        '&:hover, &:focus': {
          backgroundColor: theme.palette.common.black,
          color: theme.palette.primary.main,
        },
      },
    },
    {
      props: { color: 'secondary', variant: 'contained' },
      style: {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.common.black,
        '&:hover, &:focus': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.secondary.main,
        },
      },
    },
    {
      props: { color: 'secondary', variant: 'text' },
      style: {
        color: theme.palette.secondary.light,
      },
    },
    {
      props: { color: 'secondary', variant: 'outlined' },
      style: {
        color: theme.palette.common.black,
      },
    },
  ],
};
theme.components.MuiLink = {
  styleOverrides: {
    root: {
      color: theme.palette.common.black,
      fontWeight: 500,
    },
  },
};
theme.components.MuiTypography = {
  styleOverrides: {
    root: {
      // color: theme.palette.common.black,
    },
  },
};
export default theme;
